/**
 * Loading Modal Component
 *
 * Reusable blocking modal for long-running operations (CSV upload, image processing, etc.).
 * Prevents user navigation and shows progress indication with optional cancel button.
 * NOW THEME-AWARE
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

interface LoadingModalProps {
  visible: boolean;
  title: string;
  message: string;
  onCancel?: () => void;
  showCancelButton?: boolean;
  subMessage?: string;
  testID?: string;
}

export function LoadingModal({
  visible,
  title,
  message,
  onCancel,
  showCancelButton = false,
  subMessage,
  testID,
}: LoadingModalProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        // Only allow closing via back button if there's a cancel handler
        if (onCancel) onCancel();
      }}
      testID={testID || 'loading-modal'}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          {/* Activity indicator */}
          <View style={styles.spinnerContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>

          {/* Title */}
          <Text style={styles.title}>{title}</Text>

          {/* Message */}
          <Text style={styles.message}>{message}</Text>

          {/* Optional sub-message */}
          {subMessage && <Text style={styles.subMessage}>{subMessage}</Text>}

          {/* Cancel button (optional) */}
          {showCancelButton && onCancel && (
            <View style={styles.buttonContainer}>
              <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    dialog: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: 24,
      width: '100%',
      maxWidth: 320,
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
      shadowColor: theme.id === 'bauhaus' ? '#000' : theme.colors.primary,
      shadowOffset: theme.id === 'bauhaus' ? { width: 8, height: 8 } : { width: 0, height: 2 },
      shadowOpacity: theme.id === 'bauhaus' ? 1 : 0.25,
      shadowRadius: theme.id === 'bauhaus' ? 0 : 8,
      elevation: 5,
    },
    spinnerContainer: {
      marginBottom: 20,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
      marginBottom: 12,
      textAlign: 'center',
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    message: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      marginBottom: 8,
      textAlign: 'center',
      lineHeight: 22,
    },
    subMessage: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textTertiary,
      marginBottom: 24,
      textAlign: 'center',
      lineHeight: 20,
    },
    buttonContainer: {
      width: '100%',
    },
    cancelButton: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.backgroundElevated,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
    },
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
